import { useRouter } from 'vue-router';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { httpInstance } from '@/clients/http.request';
import { urlFiles } from '@/helpers';
import { showError, showInfo } from './useToast';

const SECCION_GRANIZO = 1;

const SECCION_LABELS = {
   1: 'Granizo',
   2: 'ART',
   3: 'Automotor',
   4: 'Caución',
   5: 'Préstamos',
   8: 'Hipoteca',
};

const SECCION_ICONS = {
   1: 'pi pi-cloud',
   2: 'pi pi-briefcase',
   3: 'pi pi-briefcase',
   4: 'pi pi-lock',
   5: 'pi pi-percentage',
   8: 'pi pi-home',
};

const SINIESTROS_ITEM = {
   label: 'Siniestros',
   icon: 'pi pi-cloud',
};

export default function useNavbar() {
   const store = useStore();
   const router = useRouter();
   const visible = ref(false);
   const showProductosDialog = ref(false);

   // Reactive properties
   const items = ref([
      {
         label: 'Menu',
         icon: 'pi pi-home',
         command: () => {
            store.commit('main/resetClientData');
            store.commit('siniestros/resetState', null);
            store.commit('automotor/resetState', null, { root: true });
            router.push({ name: 'menu' });
            visible.value = false;
         },
      },
      {
         label: 'Buscar',
         icon: 'pi pi-search',
         items: [
            {
               label: 'Buscar Póliza',
               icon: ' pi pi-bookmark-fill',
               command: () => {
                  router.push({ name: 'poliza/consulta' });
                  visible.value = false;
               },
            },
            {
               label: 'Buscar Solicitud',
               icon: 'pi pi-bookmark',
               command: () => {
                  router.push({ name: 'poliza/solicitudes' });
                  visible.value = false;
               },
            },
         ],
      },
   ]);

   // Computed properties
   const usuario = computed(() => store.state.auth.usuario);
   const sections = computed(() => store.state.main.sections || []);
   const tieneSeccionGranizo = computed(() =>
      sections.value.some(
         (seccion) => Number(seccion.id_seccion) === SECCION_GRANIZO,
      ),
   );

   const syncSiniestrosItem = () => {
      const siniestrosIndex = items.value.findIndex(
         (item) => item.label === 'Siniestros',
      );

      if (tieneSeccionGranizo.value) {
         if (siniestrosIndex >= 0) return;
         const buscarIndex = items.value.findIndex(
            (item) => item.label === 'Buscar',
         );
         const insertAt = buscarIndex >= 0 ? buscarIndex + 1 : items.value.length;
         items.value.splice(insertAt, 0, {
            ...SINIESTROS_ITEM,
            command: () => {
               router.push({ name: 'siniestros/buscar' });
               visible.value = false;
            },
         });
         return;
      }

      if (siniestrosIndex >= 0) {
         items.value.splice(siniestrosIndex, 1);
      }
   };

   watch(tieneSeccionGranizo, syncSiniestrosItem, { immediate: true });

   // Menú Bsa: empresas 20 y 50 (mismas reglas de nivel que para la 20).
   onMounted(async () => {
      const idEmpresa = usuario.value?.id_empresa;
      const esMenuBsaEmpresa = idEmpresa === 20 || idEmpresa === 50;

      if (!sections.value.length) {
         try {
            await store.dispatch('main/getInitialData');
         } catch {
            // Si falla, syncSiniestrosItem deja Siniestros oculto
         }
      }
      syncSiniestrosItem();
      if (usuario.value && esMenuBsaEmpresa) {
         if (usuario.value.nivel > 2) {
            items.value.push({
               label: 'Bsa',
               icon: 'pi pi-briefcase',
               items: [
                  {
                     label: 'Bsa',
                     icon: 'pi pi-users',
                     command: () => {
                        router.push('/multicotizador/bsa');
                        visible.value = false;
                     },
                  },
                  {
                     label: 'Gestión de prospectos',
                     icon: 'pi pi-chart-line',
                     command: () => router.push({ name: 'prospectos' }),
                  },
                  ...(usuario.value.nivel >= 4
                     ? [
                        {
                           label: 'ABMs',
                           icon: 'pi pi-database',
                           items: [
                              {
                                 label: 'Gestión de usuarios',
                                 icon: 'pi pi-users',
                                 command: () =>
                                    router.push({ name: 'usuarios' }),
                              },
                              {
                                 label: 'Gestión de regiones',
                                 icon: 'pi pi-map',
                                 command: () =>
                                    router.push({ name: 'regiones' }),
                              },
                              {
                                 label: 'Gestión de compañías',
                                 icon: 'pi pi-building',
                                 command: () =>
                                    router.push({ name: 'companias' }),
                              },
                              ...(usuario.value.nivel >= 5 &&
                              usuario.value.id_empresa === 20
                                 ? [
                                    {
                                       label: 'Bases y condiciones',
                                       icon: 'pi pi-book',
                                       command: () =>
                                          router.push({ name: 'terminos' }),
                                    },
                                 ]
                                 : []),
                              ...(usuario.value.nivel >= 6
                                 ? [
                                    {
                                       label: 'Gestión de empresas',
                                       icon: 'pi pi-briefcase',
                                       command: () =>
                                          router.push({
                                             name: 'empresas',
                                          }),
                                    },
                                    {
                                       label: 'Descuentos',
                                       icon: 'pi pi-percentage',
                                       command: () =>
                                          router.push({
                                             name: 'descuentos',
                                          }),
                                    },
                                    {
                                       label: 'Coberturas',
                                       icon: 'pi pi-shield',
                                       command: () =>
                                          router.push({
                                             name: 'coberturas',
                                          }),
                                    },
                                    {
                                       label: 'Cargar Productos',
                                       icon: 'pi pi-upload',
                                       command: () => {
                                          showProductosDialog.value = true;
                                          visible.value = false;
                                       },
                                    },
                                 ]
                                 : []),
                           ],
                        },
                     ]
                     : []),
                  ...(usuario.value.nivel === 10
                     ? [
                        {
                           label: 'Entidades del sistema',
                           icon: 'pi pi-database',
                           command: () => {
                              router.push({ name: 'entidades' });
                              visible.value = false;
                           },
                        },
                     ]
                     : []),
               ],
            });
         } else {
            items.value.push({
               label: 'Bsa',
               icon: 'pi pi-briefcase',
               items: [
                  {
                     label: 'Gestión de prospectos',
                     icon: 'pi pi-chart-line',
                     command: () => {
                        router.push({ name: 'prospectos' });
                        visible.value = false;
                     },
                  },
                  ...(usuario.value.nivel === 10
                     ? [
                        {
                           label: 'Entidades del sistema',
                           icon: 'pi pi-database',
                           command: () => {
                              router.push({ name: 'entidades' });
                              visible.value = false;
                           },
                        },
                     ]
                     : []),
               ],
            });
         }
      }

      if (
         usuario.value?.nivel >= 2 ||
         (usuario.value?.nivel >= 1 &&
            (usuario.value?.id_empresa == 20 ||
               usuario.value?.id_empresa == 50))
      ) {
         items.value.push({
            label: 'Reportes',
            icon: 'pi pi-book',
            command: () => {
               router.push({ name: 'reportes' });
               visible.value = false;
            },
         });
      }
   });

   const buildManualCommand = (archivo) => async () => {
      try {
         showInfo('Por favor, espere mientras se descarga el archivo.');
         const dl = await httpInstance.get(`${urlFiles}/manuales/download`, {
            path: archivo.path,
         });
         const signedUrl = dl.data;
         const link = document.createElement('a');
         link.href = signedUrl;
         link.setAttribute('download', archivo.nombre);
         link.style.display = 'none';
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
      } catch (error) {
         showError('No se pudo descargar el manual');
      }
      visible.value = false;
   };

   const cargarManuales = async () => {
      try {
         const response = await httpInstance.get(`${urlFiles}/manuales/list`);
         const data = response?.data;
         if (!Array.isArray(data) || data.length === 0) {
            return;
         }

         // Siempre: Comunicaciones → Sección → archivos (como el menú histórico)
         const menuItems = data
            .filter((seccion) => (seccion.archivos || []).length > 0)
            .map((seccion) => ({
               label:
                  seccion.id_seccion == null
                     ? 'Generales'
                     : SECCION_LABELS[seccion.id_seccion] ||
                       `Sección ${seccion.id_seccion}`,
               icon:
                  seccion.id_seccion == null
                     ? 'pi pi-book'
                     : SECCION_ICONS[seccion.id_seccion] || 'pi pi-briefcase',
               items: (seccion.archivos || []).map((archivo) => ({
                  label: archivo.nombre,
                  icon: 'pi pi-book',
                  command: buildManualCommand(archivo),
               })),
            }));

         if (menuItems.length === 0) {
            return;
         }

         // Evitar duplicar si se llama dos veces
         const existingIdx = items.value.findIndex(
            (item) => item.label === 'Comunicaciones',
         );
         const comunicaciones = {
            label: 'Comunicaciones',
            icon: 'pi pi-comments',
            items: menuItems,
         };

         if (existingIdx >= 0) {
            items.value.splice(existingIdx, 1, comunicaciones);
            return;
         }

         const siniestrosIndex = items.value.findIndex(
            (item) => item.label === 'Siniestros',
         );
         const buscarIndex = items.value.findIndex(
            (item) => item.label === 'Buscar',
         );
         const insertAt =
            siniestrosIndex >= 0
               ? siniestrosIndex + 1
               : buscarIndex >= 0
                 ? buscarIndex + 1
                 : items.value.length;
         items.value.splice(insertAt, 0, comunicaciones);
      } catch (error) {
         // Silencio total en listado
      }
   };

   // Exposed properties and methods
   return {
      items,
      visible,
      showProductosDialog,
      cargarManuales,
   };
}
